<template>
  <div class="panel evsrc" :class="{ folder: !connected }">
    <!-- Not connected (or cut off): the ask, with exactly what is kept -->
    <template v-if="!connected">
      <span class="lab" :class="{ red: needsReconnect }">{{ needsReconnect ? 'Google stopped letting me look' : 'Let me see your calendar' }}</span>
      <p v-if="needsReconnect" class="sm gap">{{ source.lastError || 'Connect again and I’ll pick up where I left off.' }}</p>
      <p v-else class="sm gap">
        Then I know when you last actually saw someone, and when you&#8217;re about to, without you telling me. Read-only:
        I can&#8217;t add, change or send anything there.
      </p>
      <details class="drawer">
        <summary>What I&#8217;d keep</summary>
        <p class="xs">
          From the last three months and the next two of your Google Calendar: who you were with, when, and the event&#8217;s
          name (the first 60 characters). From Google Contacts: a birthday, only for someone already in your people who
          doesn&#8217;t have one yet. Someone who turns up in two or more meetings but isn&#8217;t in your people gets named
          in a suggestion to you, nothing more. Everything else is read once and dropped. I read again each morning before
          I sit down.
        </p>
      </details>
      <div class="btnrow gap">
        <button class="btn" type="button" :disabled="busy" @click="connect">{{ busy ? 'Reading…' : needsReconnect ? 'Connect Google again' : 'Connect Google Calendar' }}</button>
      </div>
      <p class="xs gap">Google shows you both boxes, calendar and contacts. Untick either one.</p>
    </template>

    <!-- Connected: what it read, what it learned, how to stop it -->
    <template v-else>
      <span class="lab green">Reading your calendar</span>
      <p class="sm gap">
        {{ readingWhat }}<template v-if="source.account"> as <span class="acct">{{ source.account }}</span></template>.
        <template v-if="source.lastSyncAt">Last read {{ ago(source.lastSyncAt) }}.</template>
      </p>
      <ul v-if="learned.length" class="ev learned">
        <li v-for="p in learned" :key="p.id">
          <router-link :to="`/people/${p.id}`">{{ p.name }}</router-link>
          <span class="xs">{{ p.line }}</span>
        </li>
      </ul>
      <p v-else-if="source.lastSyncAt" class="xs gap">
        Nothing on it names anyone in your people yet. If they&#8217;re in an event title or on the invite, I&#8217;ll find them.
      </p>
      <p v-if="source.lastError" class="xs gap warn">{{ source.lastError }}</p>
      <div class="btnrow gap">
        <button class="btn ghost" type="button" :disabled="busy" @click="readNow">{{ busy ? 'Reading…' : 'Read it again now' }}</button>
        <button class="btn quiet" type="button" :disabled="busy" @click="disconnect">Stop reading it</button>
      </div>
      <p class="xs gap">Stopping withdraws Google&#8217;s permission. What I already learned stays on their files, where you can strike it out.</p>
    </template>
  </div>
</template>

<script>
import { db, auth } from '@/firebase';
import { collection, doc, onSnapshot, query, where } from 'firebase/firestore';
import { connectGoogleEvidence, syncGoogleEvidence, disconnectGoogleEvidence } from '@/api';
import { requestGoogleCode } from '@/services/googleCode';

const DAY = 24 * 60 * 60 * 1000;
const days = (n) => (n === 1 ? '1 day' : `${n} days`);

export default {
  name: 'EvidenceSourcePanel',
  props: { uid: { type: String, required: true } },
  data() {
    return { source: {}, people: [], busy: false, unsubs: [] };
  },
  computed: {
    connected() {
      return this.source.status === 'connected';
    },
    needsReconnect() {
      return this.source.status === 'needs_reconnect';
    },
    readingWhat() {
      const s = this.source.scopes || {};
      if (s.calendar && s.contacts) return 'Your Google Calendar and Contacts';
      return s.contacts ? 'Your Google Contacts (not the calendar)' : 'Your Google Calendar (not contacts)';
    },
    // What the calendar has told me, in its terms: last seen, next seen.
    learned() {
      const now = Date.now();
      return this.people
        .filter((p) => p.evidence && (p.evidence.lastSeenAt || p.evidence.nextSeenAt))
        .map((p) => {
          const ev = p.evidence;
          const parts = [];
          if (ev.lastSeenAt) {
            const d = Math.floor((now - ev.lastSeenAt) / DAY);
            parts.push(d <= 0 ? 'saw them today' : `saw them ${days(d)} ago`);
          }
          if (ev.nextSeenAt && ev.nextSeenAt >= now) {
            const d = Math.ceil((ev.nextSeenAt - now) / DAY);
            parts.push(d <= 1 ? 'seeing them tomorrow' : `seeing them in ${days(d)}`);
          }
          return { id: p.id, name: p.name, line: parts.join(' · '), sort: ev.lastSeenAt || 0 };
        })
        .sort((a, b) => b.sort - a.sort)
        .slice(0, 6);
    },
  },
  watch: {
    uid: { immediate: true, handler(uid) { this.teardown(); if (uid) this.subscribe(uid); } },
  },
  beforeUnmount() {
    this.teardown();
  },
  methods: {
    subscribe(uid) {
      this.unsubs.push(onSnapshot(doc(db, 'evidenceSources', uid), (snap) => {
        this.source = (snap.exists() && snap.data().google) || {};
      }, () => { this.source = {}; }));
      this.unsubs.push(onSnapshot(query(collection(db, 'relationships'), where('userId', '==', uid), where('status', '==', 'active')), (snap) => {
        this.people = snap.docs.map((d) => ({ id: d.id, name: String(d.data().name || 'Someone'), evidence: d.data().evidence || null }));
      }, () => { this.people = []; }));
    },
    teardown() {
      this.unsubs.forEach((u) => u());
      this.unsubs = [];
    },
    ago(t) {
      const d = Math.floor((Date.now() - t) / DAY);
      if (d <= 0) return 'today';
      return d === 1 ? 'yesterday' : `${d} days ago`;
    },
    message(e, fallback) {
      return (e && e.message && !/^internal$/i.test(e.message)) ? e.message : fallback;
    },
    async connect() {
      this.busy = true;
      try {
        const user = auth.currentUser;
        const code = await requestGoogleCode({ loginHint: user && user.email });
        if (!code) return; // they closed Google's window: nothing happened
        const res = await connectGoogleEvidence({ code });
        const r = (res && res.data && res.data.lastResult) || null;
        if (res && res.data && res.data.firstReadFailed) this.$toast.info('Connected. The first read didn’t finish; I’ll try again in the morning.');
        else this.$toast.success(r ? `Read ${r.eventsRead} events. ${r.peopleUpdated === 1 ? 'One of your people' : `${r.peopleUpdated} of your people`} updated.` : 'Connected.');
      } catch (e) {
        this.$toast.error(this.message(e, 'Couldn’t connect Google.'));
      } finally {
        this.busy = false;
      }
    },
    async readNow() {
      this.busy = true;
      try {
        const res = await syncGoogleEvidence();
        const r = res && res.data && res.data.lastResult;
        this.$toast.success(r && r.signalsNew ? `${r.signalsNew} new ${r.signalsNew === 1 ? 'thing' : 'things'} from your calendar.` : 'Read it. Nothing new.');
      } catch (e) {
        this.$toast.error(this.message(e, 'Couldn’t read it just now.'));
      } finally {
        this.busy = false;
      }
    },
    async disconnect() {
      this.busy = true;
      try {
        await disconnectGoogleEvidence();
        this.$toast.success('Stopped. Google’s permission is withdrawn.');
      } catch (e) {
        this.$toast.error(this.message(e, 'Couldn’t disconnect just now.'));
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>

<style scoped>
.gap { margin-top: 0.6rem; }
.evsrc details.drawer { margin-top: 0.6rem; }
.evsrc .acct { overflow-wrap: anywhere; }
.learned { margin-top: 0.6rem; }
.learned li { display: flex; flex-direction: column; gap: 0.1rem; }
.warn { color: var(--red-ink); }
</style>
